import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { selectAdminToken, selectOrganizationId, selectSessionId } from './sessionSlice';
import { setThemeAssetBaseUrl } from '../utils/s3PublicUrl';


export const fetchThemeData = createAsyncThunk(
  'api/fetchThemeData',
  async (
    { themeId = null, isAdmin = false },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const adminToken = selectAdminToken(state);
      let organizationId = selectOrganizationId(state);
      let sessionId = selectSessionId(state);

      // For admin pages, keep the actual sessionId from Redux
      // (superadmin will have 'admin', regular admin will have their actual sessionId)
      // Only use organizationId fallback if needed
      if (isAdmin) {
        organizationId = organizationId || 'admin';
      }

      // Guard: don't send request if token is missing or malformed — backend middleware
      // requires "Bearer sessionId&organizationId" and returns 400 otherwise.
      if (!adminToken || !adminToken.includes('&')) {
        return rejectWithValue('No valid session token');
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/fetchThemeData`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            currentTheme: themeId,
            organizationId,
            sessionId,
          }),
        }
      );

      if (!response.ok) throw new Error('Network response was not ok');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const _rawTheme = localStorage.getItem('currentTheme');
// guard against old bug that stored the literal string "undefined"
if (_rawTheme === 'undefined') localStorage.removeItem('currentTheme');
const persistedTheme = _rawTheme !== 'undefined' ? _rawTheme : null;

const themeSlice = createSlice({
  name: 'theme',
  initialState: {
    currentTheme: persistedTheme || null,
    availableThemes: [],
    status: 'idle',
    error: null,
    data: null,
    /** Ignore fulfilled/rejected from older requests when a newer fetchThemeData was dispatched */
    latestFetchRequestId: null,
  },
  reducers: {
    selectTheme: (state, action) => {
      state.currentTheme = action.payload;
      localStorage.setItem('currentTheme', action.payload);
    },
    resetTheme: (state) => {
      state.currentTheme = null;
      localStorage.removeItem('currentTheme');
    },
    /** Patch theme payload after an admin section save so Redux matches DB without a full refetch. */
    mergeThemeData: (state, action) => {
      const patch = action.payload;
      if (!patch || typeof patch !== 'object') return;
      const base = state.data && typeof state.data === 'object' ? state.data : {};
      const next = { ...base, ...patch };
      if (patch.intro && typeof patch.intro === 'object') {
        next.intro = { ...(base.intro || {}), ...patch.intro };
      }
      if (patch.thankyou && typeof patch.thankyou === 'object') {
        next.thankyou = { ...(base.thankyou || {}), ...patch.thankyou };
      }
      if (patch.pages_bg && typeof patch.pages_bg === 'object') {
        next.pages_bg = { ...(base.pages_bg || {}), ...patch.pages_bg };
      }
      if (patch.stages_data && typeof patch.stages_data === 'object') {
        const prevStages = base.stages_data && typeof base.stages_data === 'object' ? base.stages_data : {};
        const stages = { ...prevStages };
        for (const [key, val] of Object.entries(patch.stages_data)) {
          if (val && typeof val === 'object' && !Array.isArray(val) && stages[key] && typeof stages[key] === 'object') {
            stages[key] = { ...stages[key], ...val };
          } else if (val != null) {
            stages[key] = val;
          }
        }
        next.stages_data = stages;
      }
      state.data = next;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchThemeData.pending, (state, action) => {
        state.latestFetchRequestId = action.meta.requestId;
        state.status = 'loading';
      })
      .addCase(fetchThemeData.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestFetchRequestId) {
          return;
        }
        state.status = 'succeeded';
        state.error = null;
        state.data = action.payload.data;
        setThemeAssetBaseUrl(action.payload.data?.asset_base_url);

        const themeName =
          action.payload.data?.themename ?? action.payload.data?.themeName;
        if (themeName && themeName !== 'undefined') {
          state.currentTheme = themeName;
          localStorage.setItem('currentTheme', themeName);
        }

        if (action.meta.arg.isAdmin) {
          state.availableThemes = action.payload.availableThemes || [];
        }
      })
      .addCase(fetchThemeData.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestFetchRequestId) {
          return;
        }
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { selectTheme, resetTheme, mergeThemeData } = themeSlice.actions;
export default themeSlice.reducer;
